import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../service/API';
import SearchableDropdown from '../TicketComponents/SearchableDropdown';
import { getApiErrorMessage } from '../../service/errorHandler';

const AssignRolePage = () => {
  const apiClient = useApiClient();
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [usersResponse, rolesResponse] = await Promise.all([
        apiClient.get('/users/detail-list'),
        apiClient.get('/roles'),
      ]);
      setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
      setAvailableRoles(Array.isArray(rolesResponse.data) ? rolesResponse.data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to load users or roles.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUserChange = (email) => {
    setSelectedEmail(email);
    const user = users.find((item) => item.email === email);
    setSelectedRoles((user?.roles || []).map((role) => role.name));
    setMessage('');
    setError('');
  };

  const toggleRole = (event) => {
    const { value, checked } = event.target;
    setSelectedRoles((current) =>
      checked ? [...new Set([...current, value])] : current.filter((role) => role !== value),
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setError('');

    if (!selectedEmail) {
      setError('Select a user.');
      return;
    }
    if (selectedRoles.length === 0) {
      setError('Select at least one role.');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.put('/users/manage', {
        selectedUser: selectedEmail,
        role: selectedRoles,
      });
      setMessage('User roles updated successfully.');
      await loadData();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to update user roles.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
        <div>
          <h1 className="mb-1">Assign Role</h1>
          <p className="text-muted mb-0">Replace the selected user&apos;s current role list.</p>
        </div>
        <Link className="btn btn-outline-secondary" to="/manage-user">Back</Link>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? (
            <div className="text-muted">Loading users and roles...</div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label" htmlFor="assignRoleUser">User</label>
                <SearchableDropdown
                  id="assignRoleUser"
                  options={users}
                  selectedValue={selectedEmail}
                  onSelectedChange={handleUserChange}
                  placeholder="Select user"
                />
              </div>

              <fieldset className="mb-3" disabled={!selectedEmail}>
                <legend className="h6">Roles</legend>
                <div className="row">
                  {availableRoles.map((role) => (
                    <div className="col-12 col-sm-6 col-lg-4" key={role.id || role.name}>
                      <div className="form-check mb-2">
                        <input
                          id={`assign-role-${role.id || role.name}`}
                          className="form-check-input"
                          type="checkbox"
                          value={role.name}
                          checked={selectedRoles.includes(role.name)}
                          onChange={toggleRole}
                        />
                        <label className="form-check-label" htmlFor={`assign-role-${role.id || role.name}`}>
                          {role.name}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </fieldset>

              <button className="btn btn-primary" type="submit" disabled={!selectedEmail || submitting}>
                {submitting ? 'Updating...' : 'Update Roles'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignRolePage;